import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { TESTIDS } from "@/constants/testIds";
import { Plus, Building2, Package, MessageSquare, FileText, ExternalLink, Trash2, StickyNote, Truck, ChevronDown, ChevronRight, AlertCircle, Search, X as XIcon, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useConfirm } from "@/components/ConfirmModal";
import { useAuth } from "@/lib/auth";
import { useAppSettings } from "@/hooks/useAppSettings";
import Modal from "@/components/Modal";
import MessageActions, { EditedTag } from "@/components/MessageActions";

const STAGES = ["Orders In", "Picking", "To Invoice", "Completed"];
const STAGE_LABEL_FALLBACK = {
  "Orders In":  "Orders In",
  "Picking":    "Picking",
  "To Invoice": "To Invoice",
  "Completed":  "Completed / Archived",
};

const STAGE_ACCENT = {
  "Orders In":  "border-l-blue-500",
  "Picking":    "border-l-amber-500",
  "To Invoice": "border-l-purple-500",
  "Completed":  "border-l-green-600",
};
const STAGE_DOT = {
  "Orders In":  "bg-blue-500",
  "Picking":    "bg-amber-500",
  "To Invoice": "bg-purple-500",
  "Completed":  "bg-green-600",
};

export default function PipelinePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const settings = useAppSettings();
  const canManage = ["admin", "user", "admin_reseller"].includes(user.role);

  // Apply admin-defined stage order (filtered to the 4 Pipeline-visible stages)
  // and labels. Falls back to canonical when settings haven't loaded yet.
  const orderedStages = useMemo(() => {
    const fromSettings = Array.isArray(settings?.Order_Stages) ? settings.Order_Stages : [];
    const allowed = new Set(STAGES);
    const ordered = fromSettings.filter((s) => allowed.has(s));
    // Append any missing canonical stages at the end for safety.
    STAGES.forEach((s) => { if (!ordered.includes(s)) ordered.push(s); });
    return ordered;
  }, [settings?.Order_Stages]);
  const labelFor = (stage) =>
    settings?.Order_Stage_Labels?.[stage] || STAGE_LABEL_FALLBACK[stage] || stage;

  const [orders, setOrders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [docketsOpen, setDocketsOpen] = useState(() => {
    const saved = typeof window !== "undefined" && localStorage.getItem("pipeline-dockets-open");
    return saved === null ? true : saved === "1";
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [chatOrder, setChatOrder] = useState(null);
  const [noteOrder, setNoteOrder] = useState(null);  // currently-edited note popover
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // Pipeline-wide search across order number, product/customer name, company.
  const [searchQ, setSearchQ] = useState("");
  // The Completed / Archived column gets noisy fast. Allow collapsing it to
  // a narrow vertical strip; user preference is sticky across sessions.
  const [completedMinimised, setCompletedMinimised] = useState(() => {
    const saved = typeof window !== "undefined" && localStorage.getItem("pipeline-completed-minimised");
    return saved === "1";
  });
  const toggleCompletedMinimised = () => {
    setCompletedMinimised((v) => {
      const next = !v;
      try { localStorage.setItem("pipeline-completed-minimised", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const onDelete = async (orderId, e) => {
    e?.stopPropagation();
    const ok = await confirm({
      title: "Delete this order card?",
      message: "The order will be permanently removed from the pipeline.",
      confirmLabel: "Delete order",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/orders/${orderId}`);
      setOrders((prev) => prev.filter((x) => x.id !== orderId));
      toast.success("Order deleted.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  const openNote = (o, e) => {
    e?.stopPropagation();
    setNoteOrder(o);
    setNoteDraft(o.Notes || "");
  };

  const saveNote = async () => {
    if (!noteOrder) return;
    setSavingNote(true);
    try {
      const { data } = await api.patch(`/orders/${noteOrder.id}`, { Notes: noteDraft });
      setOrders((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setNoteOrder(null);
      toast.success("Note saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSavingNote(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [o, c, p, d] = await Promise.all([
        api.get("/orders"), api.get("/companies"), api.get("/inventory"), api.get("/dockets"),
      ]);
      setOrders(o.data); setCompanies(c.data); setProducts(p.data); setDockets(d.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleDockets = () => {
    setDocketsOpen((v) => {
      const next = !v;
      try { localStorage.setItem("pipeline-dockets-open", next ? "1" : "0"); } catch (_) {}
      return next;
    });
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const upcomingDockets = useMemo(() =>
    (dockets || [])
      .filter((d) => d.Docket_Status === "Pending" || d.Docket_Status === "Scheduled")
      .sort((a, b) => (a.Pickup_Deadline || "").localeCompare(b.Pickup_Deadline || ""))
      .slice(0, 6),
    [dockets]
  );
  const overdueCount = upcomingDockets.filter((d) => (d.Pickup_Deadline || "") < todayStr).length;

  const lookups = useMemo(() => ({
    company: Object.fromEntries(companies.map(c => [c.id, c])),
    product: Object.fromEntries(products.map(p => [p.id, p])),
  }), [companies, products]);

  const byStage = useMemo(() => {
    const m = Object.fromEntries(STAGES.map(s => [s, []]));
    const term = searchQ.trim().toLowerCase();
    const matches = (o) => {
      if (!term) return true;
      const co = lookups.company[o.Company_ID];
      const haystack = [
        o.Order_Number,
        o.Order_Reference,
        o.Customer_Name,
        co?.Company_Name,
        ...(o.Lines || []).map(l => l.Product_Name),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    };
    orders.forEach(o => { if (m[o.Order_Status] && matches(o)) m[o.Order_Status].push(o); });
    return m;
  }, [orders, searchQ, lookups]);

  const moveOrder = async (orderId, toStage) => {
    const order = orders.find(o => o.id === orderId);
    if (!order || order.Order_Status === toStage) return;
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, Order_Status: toStage } : o));
    try {
      await api.patch(`/orders/${orderId}`, { Order_Status: toStage });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      // revert
      setOrders(prev => prev.map(o => o.id === orderId ? order : o));
    }
  };

  const onDragStart = (e, id) => {
    setDragging(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, stage) => { e.preventDefault(); setHoverCol(stage); };
  const onDrop = (e, stage) => {
    e.preventDefault();
    if (dragging) moveOrder(dragging, stage);
    setDragging(null); setHoverCol(null);
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Orders Pipeline</h1>
          <p className="text-sm text-zinc-500 mt-1">{orders.length} order{orders.length === 1 ? "" : "s"} · drag cards between stages.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="pipeline-search"
              type="search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search by order #, customer, company…"
              className="es-input pl-8 pr-8 h-9 text-sm w-72"
              autoComplete="off"
            />
            {searchQ && (
              <button
                type="button"
                onClick={() => setSearchQ("")}
                aria-label="Clear search"
                data-testid="pipeline-search-clear"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-400 hover:text-zinc-700"
              ><XIcon className="w-3.5 h-3.5" /></button>
            )}
          </div>
          {canManage && (
            <button data-testid={TESTIDS.newOrderBtn} onClick={() => setShowNew(true)} className="es-btn-primary">
              <Plus className="w-4 h-4" /> New order
            </button>
          )}
        </div>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      {/* Upcoming Dockets — collapsible, persistent open/close in localStorage */}
      <div className="es-card overflow-hidden" data-testid="pipeline-upcoming-dockets">
        <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors">
          <button
            type="button"
            onClick={toggleDockets}
            className="flex items-center gap-2 text-left"
            data-testid="pipeline-upcoming-dockets-toggle"
            aria-expanded={docketsOpen}
          >
            {docketsOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
            <Truck className="w-4 h-4 text-brand-600" />
            <span className="font-display font-semibold text-sm text-zinc-900">Upcoming Dockets</span>
            <span className="es-badge es-badge-neutral text-[10px]" data-testid="pipeline-upcoming-count">{upcomingDockets.length}</span>
            {overdueCount > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5"
                data-testid="pipeline-upcoming-overdue"
              ><AlertCircle className="w-3 h-3" /> {overdueCount} overdue</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate("/dockets")}
            className="text-xs font-semibold text-brand-700 hover:underline inline-flex items-center gap-1"
            data-testid="pipeline-upcoming-view-all"
          >
            Dispatch board <ExternalLink className="w-3 h-3" />
          </button>
        </div>
        {docketsOpen && (
          <div className="border-t border-zinc-100">
            {upcomingDockets.length === 0 ? (
              <div className="px-5 py-6 text-center text-xs font-mono text-zinc-500">No upcoming pickups scheduled.</div>
            ) : (
              <ul className="divide-y divide-zinc-50">
                {upcomingDockets.map((d) => {
                  const co = lookups.company[d.Company_ID];
                  const pr = lookups.product[d.Product_ID];
                  const overdue = (d.Pickup_Deadline || "") < todayStr;
                  return (
                    <li
                      key={d.id}
                      className="flex items-center gap-3 px-5 py-2.5 hover:bg-zinc-50 cursor-pointer"
                      data-testid={`pipeline-upcoming-row-${d.id}`}
                      onClick={() => navigate("/dockets")}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${overdue ? "bg-red-500" : d.Docket_Status === "Scheduled" ? "bg-blue-500" : "bg-amber-500"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-zinc-900 truncate">{co?.Company_Name || "—"}</div>
                        <div className="text-xs text-zinc-500 truncate">
                          {pr?.Product_Name || "—"} · {d.Quantity_To_Pick_Up} units
                          {d.Docket_Number ? ` · ${d.Docket_Number}` : ""}
                        </div>
                      </div>
                      <div className={`text-xs font-medium ml-3 flex-shrink-0 ${overdue ? "text-red-600 font-bold" : "text-zinc-500"}`}>
                        {d.Pickup_Deadline}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading pipeline…</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {orderedStages.map(stage => {
            const items = byStage[stage];
            const isHover = hoverCol === stage;
            const isCompleted = stage === "Completed";
            const minimised = isCompleted && completedMinimised;
            if (minimised) {
              return (
                <div
                  key={stage}
                  data-testid={TESTIDS.pipelineCol(stage)}
                  onDragOver={(e) => onDragOver(e, stage)}
                  onDragLeave={() => setHoverCol(null)}
                  onDrop={(e) => onDrop(e, stage)}
                  onClick={toggleCompletedMinimised}
                  title="Expand Completed column"
                  className={`w-14 flex-shrink-0 bg-zinc-50 border rounded-xl flex flex-col items-center py-3 gap-3 cursor-pointer hover:bg-white hover:border-brand-400 transition-colors ${isHover ? "border-brand-500 bg-brand-50/60" : "border-zinc-200"}`}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleCompletedMinimised(); }}
                    data-testid="pipeline-completed-expand"
                    className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-white"
                    aria-label="Expand Completed column"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                  <div className={`w-1.5 h-1.5 rounded-full ${STAGE_DOT[stage] || "bg-zinc-300"}`} />
                  <div
                    className="font-display font-semibold text-[11px] text-zinc-700 tracking-wider"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >{labelFor(stage)}</div>
                  <div className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-white text-zinc-600 border border-zinc-200 mt-auto">{items.length}</div>
                </div>
              );
            }
            return (
              <div
                key={stage}
                data-testid={TESTIDS.pipelineCol(stage)}
                onDragOver={(e) => onDragOver(e, stage)}
                onDragLeave={() => setHoverCol(null)}
                onDrop={(e) => onDrop(e, stage)}
                className={`w-72 flex-shrink-0 bg-zinc-50 border rounded-xl flex flex-col ${isHover ? "border-brand-500 bg-brand-50/60" : "border-zinc-200"}`}
              >
                <div className="px-3 py-2.5 border-b border-zinc-200/70 flex items-center justify-between">
                  <div className="font-display font-semibold text-[13px] text-zinc-700">{labelFor(stage)}</div>
                  <div className="flex items-center gap-1.5">
                    <div className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-white text-zinc-600 border border-zinc-200">{items.length}</div>
                    {isCompleted && (
                      <button
                        type="button"
                        onClick={toggleCompletedMinimised}
                        data-testid="pipeline-completed-minimise"
                        className="p-1 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-white"
                        aria-label="Minimise Completed column"
                        title="Minimise Completed column"
                      ><ChevronsLeft className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {items.map(o => {
                    const co = lookups.company[o.Company_ID];
                    const pr = lookups.product[o.Product_ID];
                    return (
                      <div
                        key={o.id}
                        data-testid={TESTIDS.pipelineCard(o.id)}
                        draggable={canManage}
                        onDragStart={(e) => onDragStart(e, o.id)}
                        className={`bg-white border border-zinc-200 border-l-4 ${STAGE_ACCENT[stage]} p-3 rounded-lg hover:border-brand-400 hover:shadow-md transition-all`}
                      >
                        <div onClick={() => navigate(`/orders/${o.id}`)} className="cursor-pointer">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="font-display font-semibold text-sm leading-tight text-zinc-900">{pr?.Product_Name || "—"}</div>
                            <div className="es-badge es-badge-brand">{o.Quantity_Ordered}</div>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-zinc-600 mb-1">
                            <Building2 className="w-3 h-3" />{co?.Company_Name || "Unknown"}
                          </div>
                          {pr?.Colour && (
                            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                              <Package className="w-3 h-3" />{pr.Colour}
                            </div>
                          )}
                          {o.Notes && (
                            <div
                              data-testid={`order-note-preview-${o.id}`}
                              className="mt-2 px-2 py-1.5 rounded-md bg-amber-50 border-l-2 border-amber-400 text-[11px] text-amber-900 italic leading-snug line-clamp-2"
                              title={o.Notes}
                            >
                              {o.Notes}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-zinc-100">
                          <button
                            data-testid={`order-chat-${o.id}`}
                            onClick={(e) => { e.stopPropagation(); setChatOrder(o); }}
                            title="Quick chat about this order"
                            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md text-zinc-500 hover:text-brand-700 hover:bg-brand-50"
                          >
                            <MessageSquare className="w-3 h-3" /> Chat
                          </button>
                          {canManage && (
                            <button
                              data-testid={`order-note-${o.id}`}
                              onClick={(e) => openNote(o, e)}
                              title={o.Notes ? "Edit internal note" : "Add internal note"}
                              className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-amber-50 ${o.Notes ? "text-amber-700" : "text-zinc-500 hover:text-amber-700"}`}
                            >
                              <StickyNote className="w-3 h-3" /> {o.Notes ? "Note" : "Add note"}
                            </button>
                          )}
                          {o.Source_PO_ID && (
                            <button
                              data-testid={`order-source-po-${o.id}`}
                              onClick={(e) => { e.stopPropagation(); navigate(`/purchase-orders?id=${o.Source_PO_ID}`); }}
                              title="View source PO"
                              className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md text-zinc-500 hover:text-brand-700 hover:bg-brand-50"
                            >
                              <FileText className="w-3 h-3" /> PO
                            </button>
                          )}
                          {user.role === "admin" && (
                            <button
                              data-testid={`order-delete-${o.id}`}
                              onClick={(e) => onDelete(o.id, e)}
                              title="Delete order"
                              className="text-xs inline-flex items-center justify-center px-1.5 py-1 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 ml-auto"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {items.length === 0 && <div className="text-[11px] text-zinc-400 text-center py-6">Empty</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <NewOrderModal
          companies={companies} products={products}
          orderedStages={orderedStages} labelFor={labelFor}
          onClose={() => setShowNew(false)}
          onCreated={(o) => { setOrders(prev => [o, ...prev]); setShowNew(false); }}
        />
      )}

      {chatOrder && (
        <OrderChatDrawer
          order={chatOrder}
          company={lookups.company[chatOrder.Company_ID]}
          product={lookups.product[chatOrder.Product_ID]}
          onClose={() => setChatOrder(null)}
        />
      )}

      {noteOrder && (
        <Modal title="Internal Note" onClose={() => setNoteOrder(null)} data-testid="order-note-modal">
          <div className="space-y-4">
            <div className="text-xs text-zinc-500">
              Visible to admin / staff only. Use this for picking instructions, pickup contacts,
              or things you don't want to send to the customer.
            </div>
            <textarea
              data-testid="order-note-textarea"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              rows={5}
              placeholder="e.g. Customer arriving 7am — leave on pallet 3, charge to credit"
              className="es-input h-auto py-2 resize-none w-full"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setNoteOrder(null)} className="es-btn-ghost">Cancel</button>
              <button
                onClick={saveNote}
                disabled={savingNote}
                data-testid="order-note-save"
                className="es-btn-primary disabled:opacity-60"
              >
                {savingNote ? "Saving…" : "Save note"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Side-drawer that opens an order-scoped chat channel ──────────────────────
function OrderChatDrawer({ order, company, product, onClose }) {
  const { user } = useAuth();
  const [channelId, setChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.post(`/orders/${order.id}/channel`).then(async ({ data }) => {
      setChannelId(data.id);
      const { data: msgs } = await api.get(`/channels/${data.id}/messages`);
      setMessages(msgs);
    });
  }, [order.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const send = async () => {
    if (!text.trim() || !channelId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/channels/${channelId}/messages`, { Body: text });
      setMessages((prev) => [...prev, data]);
      setText("");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-zinc-900/40" onClick={onClose} />
      <aside className="w-full sm:w-[420px] bg-white border-l border-zinc-200 shadow-2xl flex flex-col" data-testid="order-chat-drawer">
        <header className="px-5 py-3 border-b border-zinc-100 flex items-start justify-between gap-2">
          <div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Quick chat · Order {order.id.slice(0, 8)}</div>
            <div className="font-semibold text-sm text-zinc-900">{product?.Product_Name || "—"} × {order.Quantity_Ordered}</div>
            <div className="text-xs text-zinc-500">{company?.Company_Name || "—"}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-xl leading-none p-1">×</button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-zinc-50">
          {messages.length === 0 ? (
            <div className="text-xs text-zinc-400 text-center py-8">No messages yet — type below to ping admin.</div>
          ) : messages.map((m) => (
            <div key={m.id} className={`text-sm group ${m.Sender_ID === user.id ? "text-right" : ""}`}>
              <div className="text-[10px] text-zinc-500 mb-0.5 flex items-center gap-1 justify-end" style={m.Sender_ID === user.id ? {} : {justifyContent: "flex-start"}}>
                <span>{m.Sender_Name} · {m.created_date?.slice(11, 16)}</span>
                <EditedTag message={m} />
                <MessageActions
                  message={m}
                  user={user}
                  compact
                  onUpdated={(fresh) => setMessages((ms) => ms.map((x) => x.id === fresh.id ? fresh : x))}
                  onDeleted={(id) => setMessages((ms) => ms.filter((x) => x.id !== id))}
                  onRestored={(msg) => setMessages((ms) => {
                    const exists = ms.some((x) => x.id === msg.id);
                    const next = exists ? ms : [...ms, msg];
                    return next.slice().sort((a, b) => (a.created_date || "").localeCompare(b.created_date || ""));
                  })}
                />
              </div>
              <div className={`inline-block px-3 py-2 rounded-2xl max-w-[85%] whitespace-pre-wrap ${m.Sender_ID === user.id ? "bg-brand-600 text-white" : "bg-white border border-zinc-200"}`}>{m.Body}</div>
            </div>
          ))}
        </div>
        <footer className="border-t border-zinc-100 p-3 flex items-end gap-2 bg-white">
          <textarea
            data-testid="order-chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Message admin… (Enter sends)"
            rows={2}
            className="flex-1 es-input h-auto py-2 resize-none text-sm"
          />
          <button data-testid="order-chat-send" disabled={sending || !text.trim()} onClick={send} className="es-btn-primary h-10">Send</button>
        </footer>
      </aside>
    </div>
  );
}

export function NewOrderModal({ companies, products, orderedStages = STAGES, labelFor = (s) => STAGE_LABEL_FALLBACK[s] || s, onClose, onCreated }) {
  const [form, setForm] = useState({
    Company_ID: "", Product_ID: "", Quantity_Ordered: 0, Order_Status: "Orders In", Delivery_Address: "", Notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const { data } = await api.post("/orders", { ...form, Quantity_Ordered: Number(form.Quantity_Ordered) });
      onCreated(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Modal title="New Order" onClose={onClose} size="lg">
      <form onSubmit={onSubmit} className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="es-label">Company *</label>
            <select required value={form.Company_ID} onChange={e => u("Company_ID", e.target.value)} className="es-select w-full">
              <option value="">Select company…</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="es-label">Product *</label>
            <select required value={form.Product_ID} onChange={e => u("Product_ID", e.target.value)} className="es-select w-full">
              <option value="">Select product…</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.Product_Name}</option>)}
            </select>
          </div>
          <div>
            <label className="es-label">Quantity *</label>
            <input type="number" required min="1" value={form.Quantity_Ordered} onChange={e => u("Quantity_Ordered", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Stage</label>
            <select value={form.Order_Status} onChange={e => u("Order_Status", e.target.value)} className="es-select w-full">
              {orderedStages.map(s => <option key={s} value={s}>{labelFor(s)}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="es-label">Delivery address</label>
            <input value={form.Delivery_Address} onChange={e => u("Delivery_Address", e.target.value)} className="es-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="es-label">Notes</label>
            <textarea rows={2} value={form.Notes} onChange={e => u("Notes", e.target.value)} className="es-input h-auto py-2" />
          </div>

          {err && <div className="sm:col-span-2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="es-btn-primary">{saving ? "Saving…" : "Create order"}</button>
          </div>
      </form>
    </Modal>
  );
}
